import config, { isLocal } from '@/config';
import { isAxiosError } from 'axios';
import pick from 'lodash/pick';
import pino, { type Level, type LogFn as PinoLogFn } from 'pino';
import type { Logger, LogFn, LogContext } from '@rock/types';
import { datadogLogs } from './datadog';

let mixin: object = {
    logType: 'browser',
    build: config.build,
};

export function logMixin(obj: object) {
    mixin = { ...mixin, ...obj };
}

const defaultLevel: Level = isLocal() ? 'info' : 'debug';

export const rootLogger = pino({
    name: 'root',
    level: defaultLevel,
    messageKey: 'msg',
    errorKey: 'err',
    browser: {
        asObject: true,
        formatters: {
            level: (label: string) => ({ level: label }),
        },
        write: (logObject: Record<string, any>) => {
            // eslint-disable-next-line prefer-const
            let { level, msg, err, ...context } = logObject;
            context = { ...mixin, ...context };

            // Only send useful axios error details
            if (isAxiosError(err)) {
                const { config, ...errorDetails } = err.toJSON() as any;
                context = {
                    ...context,
                    error: {
                        ...errorDetails,
                        config: pick(config, ['data', 'method', 'url', 'headers']),
                    },
                };
            }

            const ddLevel = toDatadogLogLevel(level as Level);
            datadogLogs.logger.log(msg, context, ddLevel, err);
        },
    },
});

function createLogFn(pinoFn: PinoLogFn): LogFn {
    return (msg: string, ctx?: LogContext): void => {
        if (ctx) {
            pinoFn(ctx, msg);
        } else {
            pinoFn(msg);
        }
    };
}

export default function (name: string, level: Level = defaultLevel): Logger {
    const log = rootLogger.child({ name });
    datadogLogs.createLogger(name, { level: toDatadogLogLevel(level) });
    log.level = level;

    return {
        name,
        debug: createLogFn(log.debug.bind(log)),
        info: createLogFn(log.info.bind(log)),
        warn: createLogFn(log.warn.bind(log)),
        error: createLogFn(log.error.bind(log)),
    };
}

// Squash fatal -> error, and trace -> debug
function toDatadogLogLevel(level: Level) {
    return level === 'fatal' ? 'error' : level === 'trace' ? 'debug' : level;
}
